import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecipes, createRecipe, aiGenerate } from '@recipe/shared';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import toast from 'react-hot-toast';
import RecipeForm from '../components/RecipeForm';
import { calculateAndSaveNutrition } from '../utils/nutritionBackground';
import { uploadStepImage, deleteStepImage, uploadCoverImage } from '../utils/imageUpload';

import { Bot, Loader2, Sparkles } from 'lucide-react';
import './AddRecipe.css';

const EMPTY_STEP = { ingredients: [{ name: '', weight: '' }], details: '' };
const EMPTY_PART = { name: '', steps: [{ ...EMPTY_STEP }] };

const AddRecipe = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    servings: '',
    servingUnit: '份',
    category: '',
    coverImage: '',
    parts: [{ name: '', steps: [{ ingredients: [{ name: '', weight: '' }], details: '' }] }]
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch existing categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const allRecipes = await getRecipes();
        const categorySet = new Set();
        (allRecipes || []).forEach((r) => {
          if (r.category && r.category.trim()) {
            categorySet.add(r.category.trim());
          }
        });
        setCategories(Array.from(categorySet).sort());
      } catch (error) {
        // Error fetching categories
      }
    };
    fetchCategories();
  }, []);

  const handleGenerateRecipe = async () => {
    if (!aiPrompt.trim()) {
      toast.error('请输入菜名或食谱内容');
      return;
    }
    setIsGenerating(true);
    const loadingToast = toast.loading('AI 大厨正在思考...');
    try {
      const generated = await aiGenerate(aiPrompt);
      
      // Ensure data structure matches form requirements
      const safeParts = generated.parts?.map(part => ({
        name: part.name || '',
        steps: part.steps?.map(step => ({
          details: step.details || '',
          ingredients: step.ingredients?.length > 0 
            ? step.ingredients.map(ing => ({ name: ing.name || '', weight: ing.weight || '' }))
            : [{ name: '', weight: '' }]
        })) || [{ details: '', ingredients: [{ name: '', weight: '' }] }]
      })) || [{ name: '', steps: [{ details: '', ingredients: [{ name: '', weight: '' }] }] }];

      setFormData(prev => ({
        ...prev,
        title: generated.title || '',
        description: generated.description || '',
        category: generated.category || '',
        servings: generated.servings || '',
        parts: safeParts
      }));

      toast.success('食谱生成成功，请核对信息');
      setAiPrompt('');
    } catch (e) {
      toast.error('生成失败: ' + e.message);
    } finally {
      setIsGenerating(false);
      toast.dismiss(loadingToast);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      if (active.id.toString().startsWith('part-')) {
        const oldIndex = parseInt(active.id.split('-')[1]);
        const newIndex = parseInt(over.id.split('-')[1]);
        setFormData(prev => ({
          ...prev,
          parts: arrayMove(prev.parts, oldIndex, newIndex)
        }));
      } else if (active.id.toString().startsWith('step-')) {
        const [, partIndex, stepIndex] = active.id.split('-');
        const [, overPartIndex, overStepIndex] = over.id.split('-');
        if (partIndex === overPartIndex) {
          setFormData(prev => ({
            ...prev,
            parts: prev.parts.map((part, pIndex) =>
              pIndex === parseInt(partIndex)
                ? { ...part, steps: arrayMove(part.steps, parseInt(stepIndex), parseInt(overStepIndex)) }
                : part
            )
          }));
        }
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePartChange = (partIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, index) =>
        index === partIndex ? { ...part, [field]: value } : part
      )
    }));
  };

  const handleStepChange = (partIndex, stepIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? {
            ...part,
            steps: part.steps.map((step, sIndex) =>
              sIndex === stepIndex ? { ...step, [field]: value } : step
            )
          }
          : part
      )
    }));
  };

  const handleIngredientChange = (partIndex, stepIndex, ingredientIndex, field, value) => {
    setFormData(prev => {
      const newFormData = {
        ...prev,
        parts: prev.parts.map((part, pIndex) =>
          pIndex === partIndex
            ? {
              ...part,
              steps: part.steps.map((step, sIndex) =>
                sIndex === stepIndex
                  ? {
                    ...step,
                    ingredients: step.ingredients.map((ingredient, iIndex) =>
                      iIndex === ingredientIndex
                        ? { ...ingredient, [field]: value }
                        : ingredient
                    )
                  }
                  : step
              )
            }
            : part
        )
      };

      // Auto-add new empty row when filling the last ingredient name
      const currentStep = newFormData.parts[partIndex].steps[stepIndex];
      const lastIngredientIndex = currentStep.ingredients.length - 1;
      if (ingredientIndex === lastIngredientIndex && field === 'name' && value.trim() !== '') {
        newFormData.parts[partIndex].steps[stepIndex].ingredients.push({ name: '', weight: '' });
      }

      return newFormData;
    });
  };

  const removeIngredient = (partIndex, stepIndex, ingredientIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? {
            ...part,
            steps: part.steps.map((step, sIndex) =>
              sIndex === stepIndex
                ? { ...step, ingredients: step.ingredients.filter((_, iIndex) => iIndex !== ingredientIndex) }
                : step
            )
          }
          : part
      )
    }));
  };

  const addStep = (partIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, index) =>
        index === partIndex
          ? { ...part, steps: [...part.steps, { ingredients: [{ name: '', weight: '' }], details: '' }] }
          : part
      )
    }));
  };

  const removeStep = (partIndex, stepIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? { ...part, steps: part.steps.filter((_, sIndex) => sIndex !== stepIndex) }
          : part
      )
    }));
  };

  const addPart = () => {
    setFormData(prev => ({
      ...prev,
      parts: [...prev.parts, { name: '', steps: [{ ingredients: [{ name: '', weight: '' }], details: '' }] }]
    }));
  };

  const removePart = (partIndex) => {
    if (formData.parts.length > 1) {
      setFormData(prev => ({
        ...prev,
        parts: prev.parts.filter((_, index) => index !== partIndex)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('食谱标题是必填项');
      return;
    }

    // Auto-fill first part name with recipe title if empty
    if (formData.parts.length > 0 && !formData.parts[0].name.trim()) {
      formData.parts[0].name = formData.title;
    }

    const isValid = formData.parts.every(part =>
      part.name.trim() &&
      part.steps.length > 0
    );

    if (!isValid) {
      toast.error('每个部分必须有名称和至少一个步骤');
      return;
    }

    setLoading(true);

    try {
      const recipeData = {
        title: formData.title,
        servings: formData.servings,
        servingUnit: formData.servingUnit,
        category: formData.category,
        coverImage: formData.coverImage || '',
        parts: formData.parts,
        nutritionFacts: null,
      };

      const res = await createRecipe(recipeData);
      toast.success('食谱添加成功！');
      navigate('/');

      // Calculate nutrition facts in background
      if (res?.id) {
        calculateAndSaveNutrition(res.id, formData);
      }
    } catch (error) {
      toast.error('添加食谱失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStepImageUpload = async (partIndex, stepIndex, file) => {
    try {
      const toastId = toast.loading('上传图片中...');
      const imageUrl = await uploadStepImage(file, 'temp', partIndex, stepIndex);
      setFormData(prev => ({
        ...prev,
        parts: prev.parts.map((part, pIndex) =>
          pIndex === partIndex
            ? {
              ...part,
              steps: part.steps.map((step, sIndex) =>
                sIndex === stepIndex ? { ...step, imageUrl } : step
              )
            }
            : part
        )
      }));
      toast.dismiss(toastId);
      toast.success('图片上传成功');
    } catch (error) {
      toast.error('图片上传失败');
    }
  };

  const handleStepImageRemove = async (partIndex, stepIndex) => {
    const imageUrl = formData.parts[partIndex]?.steps[stepIndex]?.imageUrl;
    if (imageUrl) {
      await deleteStepImage(imageUrl);
    }
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, pIndex) =>
        pIndex === partIndex
          ? {
            ...part,
            steps: part.steps.map((step, sIndex) =>
              sIndex === stepIndex ? { ...step, imageUrl: '' } : step
            )
          }
          : part
      )
    }));
  };

  const handleCoverImageUpload = async (croppedDataUrl) => {
    setFormData(prev => ({ ...prev, coverImage: croppedDataUrl }));
    toast.success('封面图已设置');
  };

  const handleCoverImageRemove = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
  };

  return (
    <div className="container">
      <div className="add-recipe-page">
        <div className="ai-generate-card">
          <div className="ai-card-header">
            <Sparkles size={20} color="#ff6b6b" />
            <h3>AI 一键生成</h3>
          </div>
          <p className="ai-card-desc">输入一个菜名，或者粘贴一段杂乱的食谱文字，AI 会自动帮你提取并填好下方表单！</p>
          <div className="ai-input-group">
            <textarea
              className="ai-textarea"
              placeholder="例如：红烧肉怎么做？"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              disabled={isGenerating}
              rows={2}
            />
            <button 
              className="ai-generate-btn" 
              onClick={handleGenerateRecipe}
              disabled={isGenerating || !aiPrompt.trim()}
            >
              {isGenerating ? <Loader2 size={18} className="spin" /> : <Bot size={18} />}
              <span>{isGenerating ? '生成中' : '自动生成'}</span>
            </button>
          </div>
        </div>

        <RecipeForm
          formData={formData}
          onInputChange={handleInputChange}
          onPartChange={handlePartChange}
          onStepChange={handleStepChange}
          onIngredientChange={handleIngredientChange}
          onAddPart={addPart}
          onRemovePart={removePart}
          onAddStep={addStep}
          onRemoveStep={removeStep}
          onRemoveIngredient={removeIngredient}
          onStepImageUpload={handleStepImageUpload}
          onStepImageRemove={handleStepImageRemove}
          onCoverImageUpload={handleCoverImageUpload}
          onCoverImageRemove={handleCoverImageRemove}
          categories={categories}
          onCategoryChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
          sensors={sensors}
          onDragEnd={handleDragEnd}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/')}
          loading={loading}
          submitLabel="保存食谱"
          loadingLabel="保存中..."
        />
      </div>
    </div>
  );
};

export default AddRecipe;
